import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./ui/hover-card";

// Module-level cache so the glossary is fetched once per session and shared.
let _cache = null;
let _promise = null;
const _subs = new Set();
export function refreshGlossary() {
  _cache = null; _promise = null;
  _subs.forEach((fn) => fn());
}
function loadGlossary() {
  if (_cache && _cache.length) return Promise.resolve(_cache);
  if (!_promise) {
    _promise = api.get("/definitions")
      .then((r) => { _cache = r.data || []; if (!_cache.length) _promise = null; return _cache; })
      .catch(() => { _promise = null; return []; });
  }
  return _promise;
}

export function useGlossary() {
  const [terms, setTerms] = useState(_cache || []);
  useEffect(() => {
    let alive = true;
    const sync = () => loadGlossary().then((d) => { if (alive) setTerms(d); });
    sync();
    _subs.add(sync);
    return () => { alive = false; _subs.delete(sync); };
  }, []);
  return terms;
}

function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

// Parse a glossary term string into its primary name + any parenthetical
// aliases/acronyms (e.g. "Special Protection System (Remedial Action Scheme) (SPS)"
// -> core "Special Protection System", alias "Remedial Action Scheme", acronym "SPS").
function parseTerm(term) {
  let core = (term || "").trim();
  const acronyms = [], aliases = [];
  const re = /\s*\(([^)]*)\)\s*$/;
  let m;
  while ((m = re.exec(core))) {
    const inner = m[1].trim();
    if (/^[A-Z][A-Za-z0-9/&\-]{1,14}$/.test(inner) && inner === inner.toUpperCase()) acronyms.push(inner);
    else if (inner.length >= 5) aliases.push(inner);
    core = core.slice(0, m.index).trim();
  }
  return { core, acronyms, aliases };
}

// Build a single matcher + lookup maps from the glossary.
function buildIndex(terms) {
  const coreMap = new Map();  // lowercased term/alias -> def
  const acrMap = new Map();   // acronym (exact case) -> def
  const defAcr = new Map();   // def object -> its primary acronym (for first-use "Full Name (ACR)")
  const alts = [];
  const addCore = (name, d) => {
    if (!name || name.length < 5) return;
    const lc = name.toLowerCase();
    if (!coreMap.has(lc)) { coreMap.set(lc, d); alts.push(name); }
    if (/[^aeiou]y$/i.test(name)) {  // Facility -> Facilities
      const ies = name.slice(0, -1) + "ies";
      if (!coreMap.has(ies.toLowerCase())) { coreMap.set(ies.toLowerCase(), d); alts.push(ies); }
    }
  };
  for (const d of terms) {
    const { core, acronyms, aliases } = parseTerm(d.term || "");
    addCore(core, d);
    aliases.forEach((a) => addCore(a, d));
    const allAcr = acronyms.concat(d.acronym ? [d.acronym] : []);
    allAcr.forEach((a) => {
      a = (a || "").trim();
      if (a.length >= 3 && !acrMap.has(a)) { acrMap.set(a, d); alts.push(a); }
    });
    const primary = allAcr.find((a) => (a || "").trim().length >= 2);
    if (primary) defAcr.set(d, primary.trim());
  }
  if (!alts.length) return null;
  alts.sort((a, b) => b.length - a.length); // longest first → prefer the most specific term
  const re = new RegExp(`\\b(${alts.map(escapeRe).join("|")})(?:s|es|'s|\u2019s)?\\b`, "g");
  return { re, coreMap, acrMap, defAcr };
}

const STATUS_LABEL = { currently_enforceable: "Currently enforceable", future_enforceable: "Future enforceable", superseded: "Superseded", retired: "Retired" };

function TermPopover({ text, def }) {
  return (
    <HoverCard openDelay={120} closeDelay={60}>
      <HoverCardTrigger asChild>
        <span data-testid="glossary-term" className="border-b border-dotted border-indigo-400 text-indigo-800 dark:text-indigo-300 dark:border-indigo-400 cursor-help">{text}</span>
      </HoverCardTrigger>
      <HoverCardContent className="rounded-none w-80 text-sm" align="start">
        <div className="font-display font-bold text-[#071B3E] dark:text-indigo-200">{def.term}</div>
        <div className="text-[11px] text-muted-foreground mb-1">
          {STATUS_LABEL[def.status] || def.status}{def.effective_date ? ` · eff ${def.effective_date}` : ""}
        </div>
        <p className="text-xs text-muted-foreground">{def.definition || "No definition text on record."}</p>
        {def.has_future_revision && def.upcoming && (
          <p className="mt-1.5 text-[11px] text-orange-700 border-l-2 border-orange-400 pl-2">
            Revised definition effective {def.upcoming.effective_date}.
          </p>
        )}
        <div className="mt-1.5 text-[10px] text-muted-foreground/70 italic">{def.source || "NERC Glossary of Terms"}</div>
      </HoverCardContent>
    </HoverCard>
  );
}

export default function GlossaryText({ text, className }) {
  const terms = useGlossary();
  const index = useMemo(() => buildIndex(terms), [terms]);
  if (!text) return null;
  if (!index) return <span className={className}>{text}</span>;

  const { re, coreMap, acrMap, defAcr } = index;
  const out = [];
  const seen = new Set();   // defs already introduced in this block (first-use tracking)
  let last = 0, m, key = 0;
  re.lastIndex = 0;
  while ((m = re.exec(text)) !== null) {
    const full = m[0];        // includes any plural/possessive suffix
    const baseTerm = m[1];    // base defined term for lookup
    const isAcr = acrMap.has(baseTerm);
    const def = acrMap.get(baseTerm) || coreMap.get(baseTerm.toLowerCase());
    if (!def) continue; // case-mismatched acronym etc. -> leave as plain text
    if (m.index > last) out.push(text.slice(last, m.index));
    // App-wide "Full Name (ACR)" first-use rule: the first time a full-name term
    // that has an acronym appears, expand it to "Full Name (ACR)"; subsequent
    // mentions (or the acronym itself) render as-is. Uses the same popover.
    const acr = defAcr.get(def);
    let display = full;
    if (!isAcr && acr && !seen.has(def)) {
      const after = text.slice(m.index + full.length).trimStart();
      if (!after.startsWith(`(${acr})`)) display = `${full} (${acr})`;
    }
    seen.add(def);
    out.push(<TermPopover key={key++} text={display} def={def} />);
    last = m.index + full.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <span className={className}>{out}</span>;
}
